import { useCallback, useMemo } from "react";

import { useDebounce } from "use-debounce";

import type { SyncRequestsBoolExp } from "src/graphql";
import useQueryState from "src/hooks/use-query-state";
import { useLocation, useSearchParams } from "src/router";
import {
  buildSyncRunsHasuraFilter,
  hasComputedFieldPredicate,
  parseFilterDate,
  parseRowResultMetric,
  parseSyncRunsView,
  parseThreshold,
  parseThresholdComparator,
  type RowResultMetric,
  SYNC_RUNS_FILTER_PARAMS,
  SyncRunsFilter,
  type SyncRunsFilterState,
  type ThresholdComparator,
} from "src/pages/syncs/sync/utils/runs-filter";

// Thresholds are debounced so each keystroke in a number input doesn't fire a
// new pair of runs/count queries.
const THRESHOLD_DEBOUNCE_MS = 500;

type Options = {
  syncId: string;
  /**
   * Whether the granular filters are available. This gates the query, not just
   * the controls: with it off, a shared or stale URL would otherwise filter a
   * list whose filter bar (and clear button) isn't rendered.
   */
  enabled: boolean;
};

export type SyncRunsFilters = {
  /** Values for the filter controls. */
  runsFilter: SyncRunsFilter;
  rangeStart: Date | undefined;
  rangeEnd: Date | undefined;
  operationsComparator: ThresholdComparator;
  operationsThreshold: number | undefined;
  rowResultMetric: RowResultMetric;
  rowResultComparator: ThresholdComparator;
  /** Percent, 0-100. */
  rowResultThreshold: number | undefined;

  setRunsFilter: (value: SyncRunsFilter) => void;
  setRangeStart: (value: Date | undefined) => void;
  setRangeEnd: (value: Date | undefined) => void;
  setOperationsComparator: (value: ThresholdComparator) => void;
  setOperationsThreshold: (value: number | undefined) => void;
  setRowResultMetric: (value: RowResultMetric) => void;
  setRowResultComparator: (value: ThresholdComparator) => void;
  setRowResultThreshold: (value: number | undefined) => void;
  clearFilters: () => void;

  /** Whether any filter is narrowing the list, for empty states and the clear button. */
  hasActiveFilters: boolean;
  hasuraFilter: SyncRequestsBoolExp;
  /**
   * True while a computed-field predicate is active. Those are evaluated per
   * row and count(*) has no early exit, so polling the total would scan the
   * sync's entire run history.
   */
  skipRunsCount: boolean;
};

/**
 * Owns the sync runs page's filter state, which lives in the URL so filtered
 * views survive reloads and can be shared. Values are parsed defensively,
 * since anything can be typed into the address bar, and each setter clears its
 * parameter when set back to the default so an unfiltered page keeps a clean
 * URL.
 */
export const useSyncRunsFilters = ({
  syncId,
  enabled,
}: Options): SyncRunsFilters => {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  const [viewParam, setViewParam] = useQueryState("view");
  const [startedAfterParam, setStartedAfterParam] =
    useQueryState("startedAfter");
  const [startedBeforeParam, setStartedBeforeParam] =
    useQueryState("startedBefore");
  const [operationsParam, setOperationsParam] = useQueryState("operations");
  const [operationsValueParam, setOperationsValueParam] =
    useQueryState("operationsValue");
  const [rowsMetricParam, setRowsMetricParam] = useQueryState("rowsMetric");
  const [rowsParam, setRowsParam] = useQueryState("rows");
  const [rowsValueParam, setRowsValueParam] = useQueryState("rowsValue");

  const runsFilter = parseSyncRunsView(viewParam);
  const operationsComparator = enabled
    ? parseThresholdComparator(operationsParam)
    : "any";
  const operationsThreshold = enabled
    ? parseThreshold(operationsValueParam)
    : undefined;
  const rowResultMetric = parseRowResultMetric(rowsMetricParam);
  const rowResultComparator = enabled
    ? parseThresholdComparator(rowsParam)
    : "any";
  const rowResultThreshold = enabled
    ? parseThreshold(rowsValueParam)
    : undefined;

  // Memoized so the parsed Dates keep a stable identity per param value —
  // filterState depends on them, and its identity drives the caller's
  // page-reset effect.
  const rangeStart = useMemo(
    () => (enabled ? parseFilterDate(startedAfterParam) : undefined),
    [enabled, startedAfterParam],
  );
  const rangeEnd = useMemo(
    () => (enabled ? parseFilterDate(startedBeforeParam) : undefined),
    [enabled, startedBeforeParam],
  );

  const [debouncedOperationsThreshold] = useDebounce(
    operationsThreshold,
    THRESHOLD_DEBOUNCE_MS,
  );
  const [debouncedRowResultThreshold] = useDebounce(
    rowResultThreshold,
    THRESHOLD_DEBOUNCE_MS,
  );

  // The view dropdown is superseded by (and hidden behind) the granular
  // filters, so ignore its state when they're enabled — a hidden control must
  // not affect the query.
  const effectiveRunsFilter = enabled ? SyncRunsFilter.ALL_RUNS : runsFilter;

  const filterState: SyncRunsFilterState = useMemo(
    () => ({
      runsFilter: effectiveRunsFilter,
      rangeStart,
      rangeEnd,
      operationsComparator,
      operationsThreshold: debouncedOperationsThreshold,
      rowResultMetric,
      rowResultComparator,
      rowResultThreshold: debouncedRowResultThreshold,
    }),
    [
      effectiveRunsFilter,
      rangeStart,
      rangeEnd,
      operationsComparator,
      debouncedOperationsThreshold,
      rowResultMetric,
      rowResultComparator,
      debouncedRowResultThreshold,
    ],
  );

  const hasuraFilter = useMemo(
    () => buildSyncRunsHasuraFilter(syncId, filterState),
    [syncId, filterState],
  );

  const setRunsFilter = useCallback(
    (value: SyncRunsFilter) =>
      setViewParam(value === SyncRunsFilter.ALL_RUNS ? null : value),
    [setViewParam],
  );
  const setRangeStart = useCallback(
    (value: Date | undefined) =>
      setStartedAfterParam(value?.toISOString() ?? null),
    [setStartedAfterParam],
  );
  const setRangeEnd = useCallback(
    (value: Date | undefined) =>
      setStartedBeforeParam(value?.toISOString() ?? null),
    [setStartedBeforeParam],
  );
  const setOperationsComparator = useCallback(
    (value: ThresholdComparator) =>
      setOperationsParam(value === "any" ? null : value),
    [setOperationsParam],
  );
  const setOperationsThreshold = useCallback(
    (value: number | undefined) =>
      setOperationsValueParam(value === undefined ? null : String(value)),
    [setOperationsValueParam],
  );
  const setRowResultMetric = useCallback(
    (value: RowResultMetric) =>
      setRowsMetricParam(value === "rejected" ? null : value),
    [setRowsMetricParam],
  );
  const setRowResultComparator = useCallback(
    (value: ThresholdComparator) =>
      setRowsParam(value === "any" ? null : value),
    [setRowsParam],
  );
  const setRowResultThreshold = useCallback(
    (value: number | undefined) =>
      setRowsValueParam(value === undefined ? null : String(value)),
    [setRowsValueParam],
  );

  // A single update — sequential per-param setters would each read the same
  // pre-navigation search params and overwrite one another.
  const clearFilters = useCallback(
    () =>
      setSearchParams(
        (params) => {
          for (const param of SYNC_RUNS_FILTER_PARAMS) {
            params.delete(param);
          }
          return params;
        },
        { replace: true, state: location.state },
      ),
    [setSearchParams, location.state],
  );

  return {
    runsFilter,
    rangeStart,
    rangeEnd,
    operationsComparator,
    operationsThreshold,
    rowResultMetric,
    rowResultComparator,
    rowResultThreshold,

    setRunsFilter,
    setRangeStart,
    setRangeEnd,
    setOperationsComparator,
    setOperationsThreshold,
    setRowResultMetric,
    setRowResultComparator,
    setRowResultThreshold,
    clearFilters,

    hasActiveFilters:
      effectiveRunsFilter !== SyncRunsFilter.ALL_RUNS ||
      rangeStart !== undefined ||
      rangeEnd !== undefined ||
      operationsComparator !== "any" ||
      rowResultComparator !== "any",
    hasuraFilter,
    skipRunsCount: hasComputedFieldPredicate(filterState),
  };
};
